// utils/fileUtils.js

/**
 * Processes uploaded files and extracts their content
 * @param {File} file - The uploaded file
 * @returns {Promise<{content: string, fileType: string, fileName: string}>}
 */
export const processFile = async (file) => {
  const fileName = file.name;
  const fileType = getFileType(fileName);
  
  // Check if file type is supported
  if (!isSupportedFileType(fileType)) {
    throw new Error(`File format "${fileType}" is not supported. Please upload JSON, TXT, CSV, DOC, or DOCX files.`);
  }
  
  // Process file based on its type
  let content = '';
  
  try {
    if (fileType === 'json') {
      content = await processJsonFile(file);
    } else if (fileType === 'txt') {
      content = await processTextFile(file);
    } else if (fileType === 'csv') {
      content = await processCsvFile(file);
    } else if (fileType === 'doc' || fileType === 'docx') {
      content = await processDocFile(file);
    }
    
    return {
      content,
      fileType,
      fileName
    };
  } catch (error) {
    console.error(`Error processing ${fileType} file:`, error);
    throw new Error(`Failed to process ${fileType} file: ${error.message}`);
  }
};

/**
 * Extract file extension from filename
 * @param {string} fileName 
 * @returns {string} file extension
 */
const getFileType = (fileName) => {
  return fileName.split('.').pop().toLowerCase();
};

/**
 * Check if file type is supported
 * @param {string} fileType 
 * @returns {boolean}
 */
const isSupportedFileType = (fileType) => {
  const supportedTypes = ['json', 'txt', 'csv', 'doc', 'docx'];
  return supportedTypes.includes(fileType);
};

/**
 * Process JSON file and return its content as a formatted string
 * @param {File} file 
 * @returns {Promise<string>}
 */
const processJsonFile = async (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (event) => {
      try {
        const jsonData = JSON.parse(event.target.result);
        // Format JSON data as a readable string
        const formattedJson = JSON.stringify(jsonData, null, 2);
        resolve(formattedJson);
      } catch (error) {
        reject(new Error('Invalid JSON format'));
      }
    };
    
    reader.onerror = () => reject(new Error('Error reading JSON file'));
    reader.readAsText(file);
  });
};

/**
 * Process CSV file and return its content as a formatted table string
 * @param {File} file 
 * @returns {Promise<string>}
 */
const processCsvFile = async (file) => {
  // First, try to load PapaParse if not already available
  let papaParseLoaded = false;
  try {
    papaParseLoaded = await loadPapaParse();
  } catch (error) {
    console.warn('Could not load PapaParse, falling back to basic CSV parsing', error);
  }
  
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (event) => {
      try {
        const csvText = event.target.result;
        
        // Use Papa Parse for better CSV handling if available
        if (papaParseLoaded && typeof window !== 'undefined' && window.Papa) {
          window.Papa.parse(csvText, {
            header: true,
            skipEmptyLines: true,
            complete: function(results) {
              // Check for parsing errors
              if (results.errors && results.errors.length > 0) {
                console.warn('CSV parsing warnings:', results.errors);
              }
              
              // Format as a readable string with headers and values
              let formattedCsv = '';
              
              // // Add metadata
              // formattedCsv += `CSV File: ${results.data.length} rows\n`;
              
              // // Add headers
              // if (results.meta && results.meta.fields && results.meta.fields.length > 0) {
              //   formattedCsv += `Headers: ${results.meta.fields.join(', ')}\n\n`;
              // }
              
              // Convert to table format for better readability
              if (results.data.length > 0) {
                // Get all headers
                const headers = results.meta.fields || Object.keys(results.data[0]);
                
                // Add header row
                formattedCsv += headers.join(',') + '\n';
                
                // Add data rows
                results.data.forEach(row => {
                  const values = headers.map(header => row[header] || '');
                  formattedCsv += values.join(',') + '\n';
                });
              }
              
              resolve(formattedCsv);
            },
            error: function(error) {
              console.error('Papa Parse error:', error);
              // Fall back to basic parsing if Papa Parse fails
              resolve(formatCsvContent(csvText));
            }
          });
        } else {
          // Simple CSV parsing if Papa Parse is not available
          resolve(formatCsvContent(csvText));
        }
      } catch (error) {
        console.error('Error processing CSV:', error);
        // Return the raw CSV content if parsing fails
        resolve(event.target.result);
      }
    };
    
    reader.onerror = () => reject(new Error('Error reading CSV file'));
    reader.readAsText(file);
  });
};

/**
 * Simple CSV formatter without using external libraries
 * @param {string} csvContent 
 * @returns {string}
 */
const formatCsvContent = (csvContent) => {
  try {
    // Split by lines and filter out empty lines
    const lines = csvContent.split(/\r?\n/).filter(line => line.trim() !== '');
    
    if (lines.length === 0) {
      return 'Empty CSV file';
    }
    
    // Assume first line is header
    const headers = parseCSVLine(lines[0]);
    
    // Summary information
    const rowCount = lines.length - 1; // exclude header
    let formattedOutput = `CSV File with ${rowCount} rows and ${headers.length} columns\n\n`;
    
    // Add header information
    formattedOutput += `Headers: ${headers.join(', ')}\n\n`;
    
    // Add header row for table format
    formattedOutput += headers.join(',') + '\n';
    
    // Add data rows (all rows for completeness)
    for (let i = 1; i < lines.length; i++) {
      formattedOutput += lines[i] + '\n';
    }
    
    return formattedOutput;
  } catch (error) {
    console.error('Error formatting CSV:', error);
    return csvContent; // Return original content if parsing fails
  }
};

/**
 * Parse a CSV line respecting quotes and commas
 * @param {string} line 
 * @returns {string[]}
 */
const parseCSVLine = (line) => {
  const result = [];
  let current = '';
  let inQuotes = false;
  
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    
    if (char === '"') {
      // Toggle quote state
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      // End of field
      result.push(current);
      current = '';
    } else {
      // Add to current field
      current += char;
    }
  }
  
  // Add the last field
  result.push(current);
  
  return result;
};

/**
 * Process text file and return its content
 * @param {File} file 
 * @returns {Promise<string>}
 */
const processTextFile = async (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (event) => {
      resolve(event.target.result);
    };
    
    reader.onerror = () => reject(new Error('Error reading text file'));
    reader.readAsText(file);
  });
};

/**
 * Check if mammoth.js is available in the global scope
 * @returns {boolean}
 */
const isMammothAvailable = () => {
  return typeof window !== 'undefined' && window.mammoth !== undefined;
};

/**
 * Try to dynamically load mammoth.js
 * @returns {Promise<boolean>} - Whether mammoth was successfully loaded
 */
const loadMammoth = async () => {
  if (isMammothAvailable()) {
    return true;
  }

  try {
    // Try to dynamically load mammoth.js
    const mammothScript = document.createElement('script');
    mammothScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/mammoth/1.4.0/mammoth.browser.min.js';
    mammothScript.async = true;
    
    // Wait for the script to load
    await new Promise((resolveScript, rejectScript) => {
      mammothScript.onload = resolveScript;
      mammothScript.onerror = () => rejectScript(new Error('Failed to load mammoth.js'));
      document.head.appendChild(mammothScript);
    });
    
    return isMammothAvailable();
  } catch (error) {
    console.error('Error loading mammoth.js:', error);
    return false;
  }
};

/**
 * Try to dynamically load PapaParse for CSV processing
 * @returns {Promise<boolean>} - Whether PapaParse was successfully loaded
 */
const loadPapaParse = async () => {
  if (typeof window !== 'undefined' && window.Papa !== undefined) {
    return true;
  }

  try {
    // Try to dynamically load PapaParse
    const papaScript = document.createElement('script');
    papaScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/PapaParse/5.3.0/papaparse.min.js';
    papaScript.async = true;
    
    // Wait for the script to load
    await new Promise((resolveScript, rejectScript) => {
      papaScript.onload = resolveScript;
      papaScript.onerror = () => rejectScript(new Error('Failed to load PapaParse'));
      document.head.appendChild(papaScript);
    });
    
    return typeof window !== 'undefined' && window.Papa !== undefined;
  } catch (error) {
    console.error('Error loading PapaParse:', error);
    return false;
  }
};

/**
 * Process DOC/DOCX file and extract its text content
 * @param {File} file 
 * @returns {Promise<string>}
 */
const processDocFile = async (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = async (event) => {
      try {
        const arrayBuffer = event.target.result;
        
        // Try to use mammoth if available or load it
        const mammothLoaded = await loadMammoth();
        
        if (mammothLoaded) {
          // Use window.mammoth to avoid ESLint errors
          const result = await window.mammoth.extractRawText({ arrayBuffer });
          resolve(result.value);
        } else {
          // Fall back to simple extraction if mammoth is not available
          const text = extractTextFromArrayBuffer(arrayBuffer);
          resolve(text || "Text extraction from this document format is limited. Some content may be missing.");
        }
      } catch (error) {
        console.error('Error processing document:', error);
        reject(new Error('Error processing document file'));
      }
    };
    
    reader.onerror = () => reject(new Error('Error reading document file'));
    reader.readAsArrayBuffer(file);
  });
};

/**
 * Simple fallback function to try to extract text from document files
 * Not as good as mammoth.js but better than nothing
 * @param {ArrayBuffer} arrayBuffer 
 * @returns {string}
 */
const extractTextFromArrayBuffer = (arrayBuffer) => {
  try {
    // Try to extract text by converting to UTF-8
    const decoder = new TextDecoder('utf-8');
    const text = decoder.decode(new Uint8Array(arrayBuffer));
    
    // If the text seems like binary data (contains a lot of non-printable characters)
    // then return a message instead
    const nonPrintableCount = (text.match(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g) || []).length;
    if (nonPrintableCount > text.length * 0.3) {
      return "This document contains binary content that could not be fully extracted without mammoth.js. Please install the library for better results.";
    }
    
    // Clean up the text a bit
    return text
      .replace(/[\x00-\x09\x0B\x0C\x0E-\x1F\x7F]/g, '') // Remove control characters
      .replace(/\r\n|\r|\n/g, '\n') // Normalize line endings
      .replace(/\n{3,}/g, '\n\n'); // Reduce excessive line breaks
  } catch (error) {
    console.error('Error in simple text extraction:', error);
    return "";
  }
};

/**
 * Formats file content to be added to the prompt context
 * @param {Array<{content: string, fileType: string, fileName: string}>} files 
 * @returns {string}
 */
export const formatFilesForPrompt = (files) => {
  if (!files || files.length === 0) {
    return '';
  }
  
  const formattedContent = files.map(file => {
    return `--- FILE: ${file.fileName} (${file.fileType.toUpperCase()}) ---\n${file.content}\n--- END FILE ---`;
  }).join('\n\n');
  
  return `\n\nCONTEXT FROM UPLOADED FILES:\n${formattedContent}\n\n`;
};